// src/features/Invite/SetupSimulation.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader, User, Building2, Shield, Sparkles } from 'lucide-react';
import './SetupSimulation.css';

const SetupSimulation = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);

  const steps = [
    { 
      icon: User, 
      title: 'Creating your account',
      description: 'Setting up your Super Admin profile...'
    },
    { 
      icon: Building2, 
      title: 'Configuring company settings',
      description: 'Initializing company permissions and roles...'
    },
    { 
      icon: Shield, 
      title: 'Securing your account',
      description: 'Setting up security protocols and access controls...'
    },
    { 
      icon: Sparkles, 
      title: 'Almost ready!',
      description: 'Finalizing your account setup...'
    }
  ];

  useEffect(() => {
    if (step < steps.length) {
      const timer = setTimeout(() => {
        setStep(prev => prev + 1);
        setProgress(((step + 1) / steps.length) * 100);
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      setCompleted(true);
      setProgress(100);
      // Call onComplete after a delay
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1500);
    }
  }, [step]);

  return (
    <div className="setup-simulation-overlay">
      <div className="setup-simulation-card">
        {/* Logo */}
        <div className="setup-simulation-logo">
          <span className="setup-simulation-logo-icon">🏪</span>
          <span className="setup-simulation-logo-text">RetailOne</span>
        </div>

        {/* Progress Bar */}
        <div className="setup-simulation-progress">
          <div className="setup-simulation-progress-bar">
            <div 
              className="setup-simulation-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="setup-simulation-progress-text">{Math.round(progress)}%</span>
        </div>

        {/* Steps */}
        <div className="setup-simulation-steps">
          {steps.map((s, index) => {
            const isActive = index === step && !completed;
            const isCompleted = index < step || completed;
            const Icon = s.icon;

            return (
              <div 
                key={index}
                className={`setup-simulation-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="setup-simulation-step-icon">
                  {isCompleted ? (
                    <CheckCircle size={20} className="setup-simulation-step-check" />
                  ) : isActive ? (
                    <Loader size={20} className="setup-simulation-step-spinner" />
                  ) : (
                    <Icon size={20} />
                  )}
                </div>
                <div className="setup-simulation-step-content">
                  <h4>{s.title}</h4>
                  <p>{s.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Status Message */}
        <div className="setup-simulation-status">
          {completed ? (
            <div className="setup-simulation-status-success">
              <CheckCircle size={20} />
              <span>Setup complete! Redirecting...</span>
            </div>
          ) : (
            <div className="setup-simulation-status-loading">
              <Loader size={20} className="setup-simulation-status-spinner" />
              <span>Setting things up for you...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupSimulation;